/**
 * SourcesPage — tela /sources (Fontes de dados).
 *
 * Visual: fiel ao protótipo docs/execucao-projetos/_local/design/prototipo/fontes.html
 * Comportamento: preservado integralmente da versão anterior (connect/disconnect/DrivePicker).
 *
 * Isolamento:
 *   - CSS próprio via screen-fontes.css (importado no topo)
 *   - Componentes com prefixo Fontes (FontesCard, FontesAddCard)
 *   - NÃO altera global.css, AppLayout, nem outros componentes compartilhados
 */
import '../styles/screen-fontes.css';

import { useCallback, useEffect, useState } from 'react';
import { t } from '../lib/i18n';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { DriveFolderPicker } from '../components/DriveFolderPicker';
import {
  FontesCard,
  FontesAddCard,
  type FontesCardVariant,
  type FontesSourceType,
} from '../components/FontesCard';
import { apiClient } from '../lib/api-client';

/* ----------------------------------------------------------------
   Tipos da API (inalterados — mesma shape de antes)
   ---------------------------------------------------------------- */
interface ApiSourceRecord {
  readonly source_id: string;
  readonly source_type: 'instagram' | 'meta_ads' | 'google_drive';
  readonly status: 'connected' | 'error' | 'disabled' | 'pending_decrypt';
  readonly connected_at: string | null;
  readonly external_account_id: string | null;
  readonly scopes_granted: readonly string[];
  readonly folder_id?: string | null;
  readonly folder_name?: string | null;
}

interface ApiSourcesResponse {
  readonly sources: readonly ApiSourceRecord[];
}

interface ApiConnectResponse {
  readonly authorization_url: string;
  readonly state: string;
  readonly expires_at: string;
}

/* ----------------------------------------------------------------
   Shape interna do componente
   ---------------------------------------------------------------- */
interface SourceInfo {
  readonly id: string;
  readonly type: FontesSourceType;
  readonly variant: FontesCardVariant;
  readonly connectedAt: string | null;
  readonly externalAccountId: string | null;
  readonly folderId: string | null;
  readonly folderName: string | null;
}

type LoadState =
  | { kind: 'loading' }
  | { kind: 'ready'; data: readonly SourceInfo[] }
  | { kind: 'error' };

/* ----------------------------------------------------------------
   Mapeadores
   ---------------------------------------------------------------- */
function mapVariant(apiStatus: ApiSourceRecord['status']): FontesCardVariant {
  switch (apiStatus) {
    case 'connected':
      return 'connected';
    case 'error':
    case 'pending_decrypt':
      return 'disconnected';
    case 'disabled':
    default:
      return 'disconnected';
  }
}

function mapApiSource(r: ApiSourceRecord): SourceInfo {
  return {
    id: r.source_id,
    type: r.source_type,
    variant: mapVariant(r.status),
    connectedAt: r.connected_at,
    externalAccountId: r.external_account_id,
    folderId: r.folder_id ?? null,
    folderName: r.folder_name ?? null,
  };
}

/**
 * Formata o label de conta exibido no card conforme o tipo de fonte.
 *
 * - instagram: "@" + handle (ou external_account_id tal como veio da API se já tem @)
 * - meta_ads:  "Conta de anúncios · {nome}" (external_account_id = ID numérico da API)
 * - google_drive: usa folderName se disponível; caso contrário null
 *
 * FLAG: quando API retornar handle formatado separado, substituir formatação aqui.
 */
function formatAccountLabel(
  type: FontesSourceType,
  externalAccountId: string | null,
  folderName: string | null,
): string | null {
  switch (type) {
    case 'instagram': {
      if (externalAccountId === null) return null;
      // Evita duplo "@" se a API já retornar o handle com "@"
      return externalAccountId.startsWith('@') ? externalAccountId : `@${externalAccountId}`;
    }
    case 'meta_ads': {
      if (externalAccountId === null) return null;
      // ID numérico ou nome de conta — prefixar com rótulo contextual
      return `Conta de anúncios · ${externalAccountId}`;
    }
    case 'google_drive': {
      return folderName ?? null;
    }
  }
}

/**
 * Formata o label de "última coleta" a partir de connected_at.
 * FLAG: quando API retornar last_synced_at separado, usar esse campo.
 */
function formatLastSync(connectedAt: string | null): string {
  if (connectedAt === null) return t('sources.neverRan');
  try {
    const d = new Date(connectedAt);
    return d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return connectedAt;
  }
}

/**
 * Conta fontes conectadas para o page-meta.
 */
function countConnected(sources: readonly SourceInfo[]): number {
  return sources.filter((s) => s.variant === 'connected').length;
}

/* ----------------------------------------------------------------
   Componente da página
   ---------------------------------------------------------------- */
/** FAB estendido "Adicionar fonte" — fixo bottom-right, gradiente CROSS (D-06) */
function FontesFab({ onClick }: { readonly onClick: () => void }): JSX.Element {
  return (
    <button
      type="button"
      className="fontes-fab"
      onClick={onClick}
      aria-label={t('fontes.addSource')}
    >
      {/* copy inline — ícone Material Symbols Rounded */}
      <span className="fontes-fab-icon" aria-hidden="true" data-icon="add">
        add
      </span>
      <span className="fontes-fab-label">
        {/* copy inline conforme protótipo — i18n key fontes.addTitle já existe */}
        {t('fontes.addTitle')}
      </span>
    </button>
  );
}

export function SourcesPage(): JSX.Element {
  const [state, setState] = useState<LoadState>({ kind: 'loading' });
  const [confirmTarget, setConfirmTarget] = useState<SourceInfo | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [pickerSourceId, setPickerSourceId] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const res = await apiClient.get<ApiSourcesResponse>('/sources');
      setState({ kind: 'ready', data: res.sources.map(mapApiSource) });
    } catch {
      setState({ kind: 'error' });
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Handler compartilhado pelo FAB e pelo FontesAddCard.
   * FLAG: quando existir fluxo real de adicionar fonte (modal ou rota), implementar aqui.
   */
  const handleAddSource = (): void => {
    /* FLAG: abrir modal/rota de seleção de nova fonte */
  };

  const handleConnect = async (type: FontesSourceType): Promise<void> => {
    setActionError(null);
    try {
      const res = await apiClient.post<ApiConnectResponse>(`/sources/connect/${type}`);
      window.location.assign(res.authorization_url);
    } catch {
      setActionError(t('sources.connectError'));
    }
  };

  const handleDisconnect = async (): Promise<void> => {
    if (confirmTarget === null) return;
    try {
      await apiClient.request(`/sources/${confirmTarget.id}`, { method: 'DELETE' });
      setConfirmTarget(null);
      await load();
    } catch {
      setActionError(t('sources.connectError'));
      setConfirmTarget(null);
    }
  };

  /* ── Contagem de conectadas para page-meta ── */
  const connectedCount = state.kind === 'ready' ? countConnected(state.data) : 0;

  return (
    <>
      <section aria-labelledby="fontes-page-title">
        {/* ── Page Header Editorial ── */}
        <div className="fontes-page-header">
          <div>
            <div className="fontes-page-eyebrow">{t('fontes.pageEyebrow')}</div>
            <h1 id="fontes-page-title" className="fontes-page-title">
              {t('fontes.pageTitlePart1')} <em>{t('fontes.pageTitleEm')}</em>
              <br />
              {t('fontes.pageTitlePart2')}
            </h1>
            <p className="fontes-page-sub">{t('fontes.pageSub')}</p>
          </div>

          {state.kind === 'ready' ? (
            <div className="fontes-page-meta">
              <strong>{t('fontes.pageMetaConnected', { count: String(connectedCount) })}</strong>
              <br />
              {t('fontes.pageMetaLastSync')}
            </div>
          ) : null}
        </div>

        {/* ── Estados globais ── */}
        {state.kind === 'loading' ? (
          <p role="status" className="fontes-loading">
            {t('sources.loading')}
          </p>
        ) : null}

        {state.kind === 'error' ? (
          <p role="alert" className="fontes-error-page">
            {t('sources.error')}
          </p>
        ) : null}

        {actionError !== null ? (
          <p role="alert" className="fontes-error-page">
            {actionError}
          </p>
        ) : null}

        {/* ── Conteúdo principal ── */}
        {state.kind === 'ready' ? (
          <>
            {/* Chart section — placeholder estrutural.
              FLAG: substituir por gráfico real quando API retornar volume por fonte.
              Por ora renderiza apenas o container com header editorial e espaço reservado. */}
            <div className="fontes-chart-section" aria-hidden="true">
              <div className="fontes-chart-kicker">{t('fontes.chartKicker')}</div>
              <div className="fontes-chart-headline">{t('fontes.chartHeadline')}</div>
              <p className="fontes-chart-guide">{t('fontes.chartGuide')}</p>
              {/* FLAG: inserir <FontesBarChart data={...} /> quando endpoint retornar volume */}
            </div>

            {/* Grade de cards de fonte */}
            <div className="fontes-sources-grid">
              {state.data.map((source, i) => (
                <FontesCard
                  key={source.id}
                  sourceId={source.id}
                  type={source.type}
                  variant={source.variant}
                  accountLabel={formatAccountLabel(
                    source.type,
                    source.externalAccountId,
                    source.folderName,
                  )}
                  lastSyncLabel={`${t('sources.lastRun')}: ${formatLastSync(source.connectedAt)}`}
                  folderId={source.folderId}
                  folderName={source.folderName}
                  staggerIndex={i}
                  onConnect={() => void handleConnect(source.type)}
                  onDisconnect={() => setConfirmTarget(source)}
                  onPickFolder={
                    source.type === 'google_drive' ? () => setPickerSourceId(source.id) : undefined
                  }
                />
              ))}

              {/* Card "Adicionar fonte" — sempre ao final */}
              <FontesAddCard onAdd={handleAddSource} />
            </div>
          </>
        ) : null}

        {/* ── Diálogo de confirmação de desconexão (comportamento preservado) ── */}
        <ConfirmDialog
          open={confirmTarget !== null}
          title={t('sources.confirmDisconnectTitle')}
          body={t('sources.confirmDisconnectBody')}
          confirmLabel={t('sources.confirmDisconnectOk')}
          cancelLabel={t('sources.confirmDisconnectCancel')}
          onConfirm={() => void handleDisconnect()}
          onCancel={() => setConfirmTarget(null)}
        />

        {/* ── DriveFolderPicker (comportamento preservado) ── */}
        {pickerSourceId !== null ? (
          <DriveFolderPicker
            sourceId={pickerSourceId}
            onClose={() => setPickerSourceId(null)}
            onFolderSelected={() => {
              setPickerSourceId(null);
              void load();
            }}
          />
        ) : null}
      </section>

      {/* FAB estendido "Adicionar fonte" — fixo bottom-right, fora do flow do section (D-06) */}
      <FontesFab onClick={handleAddSource} />
    </>
  );
}
